"use client";

import { useIsMobile } from "@nimble/react-state-hooks/use-mobile";
import { CheckCircle2 } from "lucide-react";
import Link from "next/link";

import NimbleLogo from "@/app/(discover)/components/nimble-logo";
import Hyperlink from "@/components/shared/hyperlink/hyperlink";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { SUPPORT_EMAIL } from "@/lib/constants";
import { deprecatedBoardModalTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import routes from "@/lib/routes";
import { StateConfig, WalledStateConfig } from "@/lib/states-config";

const SIGNOFF = "Thank you for letting Nimble be part of your search.";

const preventClose = (event: { preventDefault: () => void }) =>
  event.preventDefault();

function Eyebrow({ stateConfig }: { stateConfig: StateConfig }) {
  return (
    <div className="flex items-center gap-2.5">
      <NimbleLogo className="w-[22px] sm:w-[26px] h-auto shrink-0" />
      <p className="text-[11px] font-medium tracking-[0.12em] uppercase text-ink-subtle">
        {`Nimble Discover · ${stateConfig.title}`}
      </p>
    </div>
  );
}

function Reassurance() {
  return (
    <div className="flex items-start gap-2.5">
      <CheckCircle2 className="h-[18px] w-[18px] shrink-0 mt-0.5 text-success" />
      <p className="text-sm leading-[1.55] text-ink-emphasis">
        <Hyperlink
          href={routes.getCandidateApplications()}
          className="font-semibold"
          data-testid={deprecatedBoardModalTestIds.APPLICATIONS_LINK}
        >
          Applications you&apos;ve already sent
        </Hyperlink>
        {" are still active, and your Nimble account stays open."}
      </p>
    </div>
  );
}

function Actions({ stateConfig }: { stateConfig: WalledStateConfig }) {
  return (
    <div className="flex flex-col gap-1">
      <Button
        asChild
        size="lg"
        className="w-full"
        data-testid={deprecatedBoardModalTestIds.CONTACT_ORGANIZATION_BUTTON}
      >
        <a href={`mailto:${stateConfig.contactEmail}`}>
          {`Contact ${stateConfig.educationOrganizationAbbreviation} for questions`}
        </a>
      </Button>
      <Button
        asChild
        variant="link"
        size="lg"
        className="w-full"
        data-testid={deprecatedBoardModalTestIds.BROWSE_OTHER_STATES_LINK}
      >
        <Link href={routes.getJobs()}>
          Browse teaching jobs in other states →
        </Link>
      </Button>
    </div>
  );
}

function Signoff() {
  return (
    <div className="border-t border-line-faint pt-4 space-y-2.5">
      <p className="text-[13px] leading-[1.5] text-ink-emphasis">{SIGNOFF}</p>
      <p className="text-xs leading-[1.5] text-muted-foreground">
        {"If you have technical questions for our team, contact "}
        <Hyperlink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Hyperlink>
      </p>
    </div>
  );
}

export interface DeprecatedBoardModalViewProps {
  stateConfig: WalledStateConfig;
}

export default function DeprecatedBoardModalView({
  stateConfig,
}: DeprecatedBoardModalViewProps) {
  const isMobile = useIsMobile();

  const headline = `${stateConfig.title}'s statewide teacher job board has closed`;
  const body = `The ${stateConfig.educationOrganizationName} has ended its sponsorship of Nimble, so this job board is no longer available.`;

  if (isMobile) {
    return (
      <Drawer open dismissible={false}>
        <DrawerContent
          showCloseButton={false}
          onEscapeKeyDown={preventClose}
          className="[&>div:first-child]:hidden"
          data-testid={deprecatedBoardModalTestIds.MODAL}
        >
          <DrawerHeader className="gap-3.5 text-left">
            <Eyebrow stateConfig={stateConfig} />
            <DrawerTitle className="text-2xl leading-[1.2] tracking-[-0.02em] font-bold text-foreground">
              {headline}
            </DrawerTitle>
            <DrawerDescription className="text-[15px] leading-[1.6] text-ink-emphasis">
              {body}
            </DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-4">
            <Reassurance />
          </div>
          <div className="shrink-0 border-t border-line-faint p-4 space-y-4">
            <Actions stateConfig={stateConfig} />
            <Signoff />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={preventClose}
        onPointerDownOutside={preventClose}
        onInteractOutside={preventClose}
        className="sm:max-w-[540px]"
        data-testid={deprecatedBoardModalTestIds.MODAL}
      >
        <DialogHeader className="gap-3.5">
          <Eyebrow stateConfig={stateConfig} />
          <DialogTitle className="text-2xl sm:text-[26px] leading-[1.2] tracking-[-0.02em] font-bold text-foreground">
            {headline}
          </DialogTitle>
          <DialogDescription className="text-base leading-[1.6] text-ink-emphasis">
            {body}
          </DialogDescription>
        </DialogHeader>
        <Reassurance />
        <Actions stateConfig={stateConfig} />
        <Signoff />
      </DialogContent>
    </Dialog>
  );
}
